import { useMutation } from '@apollo/client';
import { CloseSharp } from '@material-ui/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import {
  CreateCompanyMutation,
  CreateCompanyMutationVariables,
  CreateLeadBookingMutation,
  CreateLeadBookingMutationVariables,
  CustomerOnboardingState,
  LeadPaymentMutation,
  LeadPaymentMutationVariables,
  Role,
  User,
} from 'lib/generated/graphql';
import { CREATE_COMPANY, CREATE_LEAD_BOOKING, LEAD_PAYMENT } from 'lib/mutations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import Tick from 'public/images/icons/tick.svg';
import { useEffect, useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import { useNotification } from 'src/hooks';
import FounderForm from 'src/screens/invite-signup/components/FounderForm';
import { Avatar, Modal } from 'src/shared-components';
import { Button } from 'src/shared-components/';
import useAuth from 'src/shared-components/Auth';
import LoadingOverlay from 'src/shared-components/Loading/LoadingOverlay';
import CheckoutForm from './CheckoutForm';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string);

const BookingModal = ({ modalRef, lead, user }: { modalRef: any; lead: User; user: User }) => {
  const { signUp, update: updateUser, signOut } = useAuth();
  const [createCompanyMutation] = useMutation<CreateCompanyMutation, CreateCompanyMutationVariables>(CREATE_COMPANY);
  const [step, setStep] = useState<number | 'LOGIN'>(0);
  const [options, setOptions] = useState({ clientSecret: '', loader: 'always' });
  const [paymentId, setPaymentId] = useState('');
  const [eventId, setEventId] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const { showError, hideNotification } = useNotification();

  useEffect(() => {
    hideNotification();
  }, []);

  const [leadPayment] = useMutation<LeadPaymentMutation, LeadPaymentMutationVariables>(LEAD_PAYMENT);
  const [createLeadBooking] = useMutation<CreateLeadBookingMutation, CreateLeadBookingMutationVariables>(
    CREATE_LEAD_BOOKING,
  );

  const closeModal = () => {
    if (lead.calendlyConnected && step === 1) {
      axios.post(`${process.env.API_BASE_URL}/calendly/cancel-event`, null, {
        params: {
          eventId,
          leadId: lead.id,
        },
      });
    }
    modalRef.current.close();
    setTimeout(() => setStep(0), 500);
  };

  const founderSignup = async (values: any) => {
    setModalLoading(true);
    hideNotification();

    try {
      if (user) {
        await signOut();
      }
      await signUp({
        email: values.email,
        name: `${values.firstName} ${values.lastName}`,
        password: values.password,
        phoneNumber: values.phoneNumber ? values.phoneNumber : null,
        role: Role.Customer,
      });
      global?.analytics?.track(`Founder Sign Up Step 1`, {
        name: `${values.firstName} ${values.lastName}`,
        email: values.email,
        phoneNumber: values.phoneNumber,
      });
      await createCompanyMutation({
        variables: {
          createCompanyData: {
            name: values.companyName,
          },
        },
      });
      await updateUser({
        customerOnboarding: {
          update: {
            state: {
              set: CustomerOnboardingState.FirstProject,
            },
            referral: {
              set: 'Member',
            },
          },
        },
      });
      setModalLoading(false);
    } catch (err: any) {
      if (err.message === 'Argument Validation Error')
        showError({ title: 'Email Already Exists', description: 'Please try another email' });
      const errs: string[] = [];
      err.graphQLErrors.forEach((item: Record<string, any>) => {
        if (item.message === 'Argument Validation Error') {
          const validationErrors = item.extensions.exception.validationErrors;
          validationErrors.forEach((validationError: Record<string, any>) => {
            if (Object.prototype.hasOwnProperty.call(validationError.constraints, 'IsEmailAlreadyExistConstraint')) {
              errs.push(validationError.constraints.IsEmailAlreadyExistConstraint);
            }
          });
        }
      });
      setModalLoading(false);
    }
  };

  useCalendlyEventListener({
    onEventScheduled: async e => {
      const event = e.data.payload.event.uri.split('/scheduled_events/')[1];
      setEventId(event);
      setModalLoading(true);

      await leadPayment({
        variables: {
          leadId: lead.id,
        },
        onCompleted(data) {
          setOptions({ clientSecret: data.leadPayment.clientSecret, loader: 'always' });
          setPaymentId(data.leadPayment.id);
        },
      });
      setStep(1);
      setModalLoading(false);
    },
    onDateAndTimeSelected(e) {
      if (!user || user.role === Role.Builder) {
        setModalLoading(true);
        setStep('LOGIN');
        setTimeout(() => {
          setModalLoading(false);
        }, 500);
      }
    },
  });

  const confirmPayment = async () => {
    const data: any = { builderId: lead.id, customerId: user.id, paymentId, eventId };

    if (lead.calendlyConnected) {
      const calEvent = await axios.get(`${process.env.API_BASE_URL}/calendly/event`, {
        params: {
          eventId,
          leadId: lead.id,
        },
      });
      data.date = DateTime.fromISO(calEvent.data.resource.start_time).toJSDate();
    }

    await createLeadBooking({
      variables: {
        data,
      },
    });
    setStep(2);
    setModalLoading(false);
  };

  return (
    <>
      <Modal ref={modalRef} maxHeight={false} closeOnBackdropClick={false} needsPadding={false}>
        {user?.role === Role.Builder && (
          <div className="relative">
            <CloseSharp onClick={closeModal} className="top-4 right-2 absolute cursor-pointer" />
            <FounderForm
              onFormSubmitted={founderSignup}
              title="Create a customer account to continue."
              subtitle="Looks like you’re logged in as a builder."
            />
          </div>
        )}
        {user?.role !== Role.Builder && (
          <div className="md:w-[540px] px-4">
            {modalLoading && <LoadingOverlay />}
            <>
              {step === 'LOGIN' && (
                <div className="relative">
                  <CloseSharp onClick={closeModal} className="top-4 right-2 absolute cursor-pointer" />
                  <FounderForm
                    onFormSubmitted={founderSignup}
                    title="Join Huddle to reserve time."
                    subtitle="You need to be a Huddle user."
                  />
                </div>
              )}

              {step !== 'LOGIN' && (
                <div className="border-b px-3 py-3 items-center text-[28px] font-medium flex gap-x-2">
                  Book 1 hour with <Avatar size="xxsmall" image={lead?.photoUrl} /> {lead?.name.split(' ')[0]}
                  <CloseSharp onClick={closeModal} className="ml-auto cursor-pointer" />
                </div>
              )}

              {step === 0 &&
                (lead?.calendlyConnected ? (
                  <InlineWidget url={lead?.calendarLink} pageSettings={{ hideEventTypeDetails: true }} />
                ) : (
                  <div className="p-4 my-8 text-lg">Sorry, this user is unavailable at this time for booking.</div>
                ))}

              {step === 1 && (
                <div className="mt-4 flex flex-col justify-center">
                  <Elements stripe={stripePromise} options={options}>
                    <CheckoutForm onSuccess={confirmPayment} setIsLoading={setModalLoading} lead={lead} />
                  </Elements>
                </div>
              )}

              {step === 2 && (
                <div className="justify-center px-16 py-16 flex flex-col items-center">
                  <Tick className="w-[120px] h-[120px]" />
                  <p className="pt-6 text-black font-nn-nouvelle-grotesk font-medium text-[36px] leading-120 tracking-tighter">
                    Payment confirmed!
                  </p>
                  <Link href={`/my-huddles`} passHref>
                    <Button
                      size="xl"
                      color="deep-black"
                      className="w-full justify-center rounded-[16px] mt-4"
                      onClick={() => modalRef?.current?.close()}
                    >
                      <p className="leading-150 font-normal tracking-tighter text-[#EAE4F6]">Go to My Huddles</p>
                    </Button>
                  </Link>
                </div>
              )}
            </>
          </div>
        )}
      </Modal>
    </>
  );
};

export default BookingModal;
